import React, { useEffect, useRef } from 'react';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler,
} from 'chart.js';

// Register Chart.js components
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler
);

export interface ChartDataset {
  labels: string[];
  values: number[];
}

export interface AdminLineChartProps {
  dataset: ChartDataset;
  color: string;
  tooltipLabel: string;
  height?: number;
}

function hexToRgba(color: string, alpha: number): string {
  // If it's already rgb format
  if (color.startsWith('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
  }
  return color;
}

export function AdminLineChart({
  dataset,
  color,
  tooltipLabel,
  height = 200,
}: AdminLineChartProps) {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (!chartRef.current || dataset.labels.length === 0) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: dataset.labels,
        datasets: [
          {
            label: tooltipLabel,
            data: dataset.values,
            borderColor: color,
            backgroundColor: hexToRgba(color, 0.1),
            tension: 0.3,
            fill: true,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: color,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: { size: 14 },
            bodyFont: { size: 13 },
            callbacks: {
              label: (context) => `${tooltipLabel}: ${context.parsed.y}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0,
              font: { size: 12 },
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.05)',
            },
          },
          x: {
            ticks: {
              font: { size: 12 },
              maxRotation: 45,
              minRotation: 45,
            },
            grid: {
              display: false,
            },
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [dataset, color, tooltipLabel]);

  if (dataset.labels.length === 0) {
    return (
      <div
        style={{ height }}
        className="d-flex align-items-center justify-content-center text-muted"
      >
        No data
      </div>
    );
  }

  return (
    <div style={{ height }}>
      <canvas ref={chartRef} />
    </div>
  );
}

export default AdminLineChart;
